import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { get } from "lodash";
import { NotificationManager } from "react-notifications";

import PaginatedTable from "../../../sharedComponents/tableWithPagination";
import { getRecipeCommentsNotApproved, declineRecipeComment } from "../../../store/actions";
import { objToQueryString, queryStringToJSON } from "../../../utils/common";
import StarRating from "../../../sharedComponents/starRating";
import FilterPanel from "../../../sharedComponents/filterPanel";
import EditCommentPanel from "./editCommentPanel";
import Loader from "../../../sharedComponents/preloader";
import NoRecipeImage from "../../../assets/images/no-recipes-found.png";
import { RecipeKeys } from "../../../constants/recipeConstants";
import ConfirmDelete from "../../../sharedComponents/confirmDelete";

const RecipeComments = ({
	getRecipeCommentsNotApproved,
	recipeCommentsList,
	declineRecipeComment,
	recipeCommentsTotal,
	recipeCommentsPrev,
	recipeCommentsNext
}) => {
	const [comments, setComments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showLoader, setShowLoader] = useState(true);
	const [filters, setFilters] = useState({ not_approved: "True", limit: RecipeKeys.RECIPE_COMMENTS_APPROVAL_RESULTS_LIMIT });
	const [query, setQuery] = useState();

	useEffect(() => {
		setQuery(objToQueryString(filters));
	}, [filters]);

	useEffect(() => {
		if (query) {
			setComments([]);
			setShowLoader(true);
			getRecipeCommentsNotApproved(query);
		}
	}, [query, getRecipeCommentsNotApproved]);

	const refetchComments = (values = null) => {
		if (values) {
			setFilters(queryStringToJSON(`?${values}`));
		} else {
			if (query) {
				setComments([]);
				setShowLoader(true);
				getRecipeCommentsNotApproved(query);
			}
		}
	};

	useEffect(() => {
		setComments((prevState) => {
			if (recipeCommentsList !== null && recipeCommentsList.length === 0) {
				setLoading(false);
			}
			return [...prevState, ...(recipeCommentsList !== null ? recipeCommentsList : [])];
		});
	}, [recipeCommentsList]);

	useEffect(() => {
		comments && comments.length && setShowLoader(false);
	}, [comments]);

	const declineComment = (values) => {
		const successHandler = () => {
			NotificationManager.success("Comment rejected!", "", 5000);
			refetchComments();
		};
		declineRecipeComment({ ...values, is_approved: 0 }, successHandler);
	};

	const onUpdate = () => {
		toggleEditCommentPanel(false);
		refetchComments();
		setSelectedComment(null);
	};

	const [deleteItem, setDeleteItem] = useState(null);
	const [selectedComment, setSelectedComment] = useState(null);
	const [editCommentPanel, toggleEditCommentPanel] = useState(false);

	const editComment = (id) => {
		setSelectedComment(comments.filter((item) => item.id === id)[0]);
		toggleEditCommentPanel(true);
	};

	const confirmDelete = (original) => {
		setDeleteItem(original);
	};
	const confirmEvent = (yes) => {
		yes && declineComment(deleteItem);
		setDeleteItem(null);
	};

	const renderEditCommentPanel = () => {
		return selectedComment ? (
			<FilterPanel
				show={editCommentPanel}
				toggle={() => toggleEditCommentPanel(!editCommentPanel)}
				title="Approve comment"
				closeBtn>
				<EditCommentPanel
					initialValues={selectedComment}
					toggle={() => toggleEditCommentPanel(!editCommentPanel)}
					onUpdate={onUpdate}
				/>
			</FilterPanel>
		) : null;
	};

	const columns = [
		{
			Header: "Recipe",
			accessor: "recipe",
			width: 200
		},
		{
			Header: "User",
			accessor: "user",
			Cell: ({ value }) =>
				value ? (
					<div>
						{value.first_name} {value.middle_name}
					</div>
				) : null,
			width: 200
		},
		{
			Header: "Title",
			accessor: "title",
			width: 200
		},
		{
			Header: "Notes",
			accessor: "notes",
			sortable: false
		},
		{
			Header: "Rating",
			accessor: "rating",
			Cell: ({ value }) => <StarRating className="fixed-tooltip" value={value} />,
			width: 150
		},
		{
			Header: "Actions",
			accessor: "id",
			Cell: ({ value, original }) => {
				return (
					<div className="text-right">
						<button
							type="button"
							className="btn btn-success btn-ghost btn-sm btn-cell mr-2"
							onClick={() => editComment(value)}>
							<i className="icon icon-check" />
						</button>
						<button
							type="button"
							className="btn btn-danger btn-ghost btn-sm btn-cell mr-3"
							onClick={() => confirmDelete(original)}>
							<i className="icon icon-close" />
						</button>
					</div>
				);
			},
			width: 120
		}
	];

	const renderDeleteMessage = () => {
		return (
			<div>
				<h4 className="mt-2 mb-3">Are you sure you want to delete?</h4>
			</div>
		);
	};

	return (
		<section className="page page-recipe-comments">
			<div className="container">
				<h3 className="flex-fill m-0">Recipe Comments</h3>

				<div className="table-container">
					{comments && comments.length ? (
						<PaginatedTable
							data={comments}
							columns={columns}
							pageSize={RecipeKeys.RECIPE_COMMENTS_APPROVAL_RESULTS_LIMIT}
							totalItems={recipeCommentsTotal}
							prev={recipeCommentsPrev}
							next={recipeCommentsNext}
							onPagination={(event) => refetchComments(event)}
						/>
					) : null}
					{loading ? (
						<Loader show={showLoader} />
					) : (
						comments.length === 0 && (
							<div className="alert alert-info text-center w-100 my-3 no-comments-found">
								<img src={NoRecipeImage} alt="No comments found" />
								<div>No comments found!</div>
							</div>
						)
					)}
				</div>
			</div>
			{renderEditCommentPanel()}
			<ConfirmDelete item={deleteItem} message={renderDeleteMessage()} onEvent={confirmEvent} />
		</section>
	);
};

const mapStateToProps = (state) => {
	const { Recipes } = state;
	return {
		recipeCommentsList: get(Recipes, "recipeCommentsToApproveList.results", null),
		recipeCommentsTotal: get(Recipes, "recipeCommentsToApproveList.count", 0),
		recipeCommentsPrev: get(Recipes, "recipeCommentsToApproveList.previous", null),
		recipeCommentsNext: get(Recipes, "recipeCommentsToApproveList.next", null)
	};
};

const mapDispatchToProps = {
	getRecipeCommentsNotApproved,
	declineRecipeComment
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(RecipeComments);
